import { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { subscriptionsService, PlanChangePreview } from '../../lib/api/subscriptions';
import type { PlanPrice as Price } from "../../lib/api/plans";
import { ArrowRight, ArrowUp, ArrowDown, Calendar, AlertTriangle, Check } from 'lucide-react';



interface PlanSwitchModalProps {
    isOpen: boolean;
    onClose: () => void;
    targetPrice: Price;
    onSuccess: () => void;
}

export const PlanSwitchModal = ({
    isOpen,
    onClose,
    targetPrice,
    onSuccess
}: PlanSwitchModalProps) => {
    const { t } = useTranslation('common');
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState<PlanChangePreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (isOpen && targetPrice?.id) {
            loadPreview();
        }
    }, [isOpen, targetPrice?.id]);

    const loadPreview = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await subscriptionsService.getChangePlanPreview(targetPrice.id);
            setPreview(result.data);
        } catch (err: any) {
            setError(err.response?.data?.error || t('plans.loadPreviewFailed', 'Unable to load plan change details'));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        setProcessing(true);
        setError(null);
        try {
            await subscriptionsService.changePlan(targetPrice.id);
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.error || t('plans.changePlanFailed', 'Failed to change plan'));
        } finally {
            setProcessing(false);
        }
    };

    const formatPrice = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
            minimumFractionDigits: 0
        }).format(amount / 100);
    };

    const getChangeIcon = () => {
        if (!preview) return <ArrowRight className="w-5 h-5" />;
        switch (preview.changeType) {
            case 'UPGRADE':
                return <ArrowUp className="w-5 h-5 text-green-500" />;
            case 'BILLING_UPGRADE':
                return <ArrowUp className="w-5 h-5 text-blue-500" />;
            case 'DOWNGRADE':
                return <ArrowDown className="w-5 h-5 text-amber-500" />;
            case 'BILLING_DOWNGRADE':
                return <ArrowDown className="w-5 h-5 text-blue-500" />;
            default:
                return <ArrowRight className="w-5 h-5" />;
        }
    };

    const getChangeColor = () => {
        if (!preview) return 'bg-neutral-100 dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700';
        switch (preview.changeType) {
            case 'UPGRADE':
                return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
            case 'BILLING_UPGRADE':
            case 'BILLING_DOWNGRADE':
                return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
            case 'DOWNGRADE':
                return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
            default:
                return 'bg-neutral-50 dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700';
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-800">
                                {/* Header */}
                                <div className="px-6 pt-6 pb-4 border-b border-neutral-100 dark:border-neutral-800">
                                    <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                                        {t('plans.changePlan', 'Change Your Plan')}
                                    </Dialog.Title>
                                </div>

                                {/* Content */}
                                <div className="px-6 py-5 space-y-5">
                                    {loading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black dark:border-white" />
                                        </div>
                                    ) : error ? (
                                        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                                            <AlertTriangle className="w-5 h-5" />
                                            <span className="text-sm">{error}</span>
                                        </div>
                                    ) : preview ? (
                                        <>
                                            {/* Plan Comparison */}
                                            <div className={`p-4 rounded-xl border ${getChangeColor()}`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="text-center flex-1">
                                                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">{t('plans.current', 'Current')}</div>
                                                        <div className="font-semibold text-neutral-900 dark:text-neutral-100">{preview.currentPlan.name}</div>
                                                        <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                                            {formatPrice(preview.currentPlan.price, preview.currentPlan.currency)}
                                                            <span className="text-neutral-400 dark:text-neutral-500">/{t(`plans.${preview.currentPlan.interval || 'month'}`, preview.currentPlan.interval)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="px-4">
                                                        {getChangeIcon()}
                                                    </div>
                                                    <div className="text-center flex-1">
                                                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">{t('plans.new', 'New')}</div>
                                                        <div className="font-semibold text-neutral-900 dark:text-neutral-100">{preview.newPlan.name}</div>
                                                        <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                                            {formatPrice(preview.newPlan.price, preview.newPlan.currency)}
                                                            <span className="text-neutral-400 dark:text-neutral-500">/{t(`plans.${preview.newPlan.interval || 'month'}`, preview.newPlan.interval)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* What Happens */}
                                            {/* Payment Summary */}
                                            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-4 space-y-3 border border-neutral-100 dark:border-neutral-700">
                                                <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{t('plans.paymentSummary', 'Payment Summary')}</h4>

                                                {/* Effective Date Note */}
                                                <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-300 mb-2">
                                                    <Calendar className="w-4 h-4 mt-0.5 shrink-0 text-neutral-400 dark:text-neutral-500" />
                                                    <span>{preview.message}</span>
                                                </div>

                                                <div className="space-y-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                                                    {/* Due Now */}
                                                    {/* Due Now */}
                                                    <div className="flex justify-between items-center text-sm">
                                                        <span className="text-neutral-600 dark:text-neutral-300 font-medium">{t('plans.dueNow', 'Due Now')}</span>
                                                        <div className="flex items-center gap-2">
                                                            {(preview.unusedTimeAmount !== undefined && preview.unusedTimeAmount !== null && preview.unusedTimeAmount < 0) ? (
                                                                <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                                                                    {formatPrice(preview.unusedTimeAmount, preview.newPlan.currency)} ({t('plans.credit', 'Credit')})
                                                                </span>
                                                            ) : (preview.prorationAmount !== null && preview.prorationAmount < 0) && (
                                                                <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                                                                    {formatPrice(preview.prorationAmount, preview.newPlan.currency)} ({t('plans.credit', 'Credit')})
                                                                </span>
                                                            )}
                                                            <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                                                                {formatPrice(preview.totalAmountDue ?? 0, preview.newPlan.currency)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Next Payment */}
                                                    {preview.nextPaymentDate && (() => {
                                                        // Calculate actual billing amount
                                                        // If interval = 'month' but billingInterval = 'year', multiply by 12
                                                        const interval = preview.newPlan.interval || 'month';
                                                        const billingInterval = preview.newPlan.billingInterval || interval;
                                                        let actualBillingAmount = preview.newPlan.price;

                                                        if (interval === 'month' && billingInterval === 'year') {
                                                            actualBillingAmount = preview.newPlan.price * 12;
                                                        } else if (interval === 'year' && billingInterval === 'month') {
                                                            actualBillingAmount = Math.floor(preview.newPlan.price / 12);
                                                        }

                                                        const billingLabel = billingInterval === 'year' ? t('plans.yearly', 'Yearly') : t('plans.monthly', 'Monthly');

                                                        return (
                                                            <div className="flex justify-between items-start text-sm">
                                                                <div className="flex flex-col">
                                                                    <span className="text-neutral-600 dark:text-neutral-300 font-medium">{t('plans.nextPayment', { frequency: billingLabel })}</span>
                                                                    <span className="text-xs text-neutral-400 dark:text-neutral-500">
                                                                        {new Date(preview.nextPaymentDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                                                    </span>
                                                                </div>
                                                                <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                                                                    {formatPrice(actualBillingAmount, preview.newPlan.currency)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>


                                        </>
                                    ) : null}
                                </div>

                                {/* Actions */}
                                <div className="px-6 py-4 bg-neutral-50 dark:bg-neutral-800 border-t border-neutral-100 dark:border-neutral-700 flex gap-3">
                                    <button
                                        onClick={onClose}
                                        className="flex-1 px-4 py-2.5 rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-medium hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                                    >
                                        {t('actions.cancel')}
                                    </button>
                                    <button
                                        onClick={handleConfirm}
                                        disabled={loading || processing || !!error}
                                        className="flex-1 px-4 py-2.5 rounded-lg bg-black dark:bg-white text-white dark:text-black font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                                    >
                                        {processing ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                                                {t('plans.processing', 'Processing...')}
                                            </>
                                        ) : (
                                            <>
                                                <Check className="w-4 h-4" />
                                                {t('plans.confirmChange', 'Confirm Change')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
